import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Divider, Table, TableBody, TableCell, TableHead, TableRow, Chip, FormControl, InputLabel, Select, MenuItem, useTheme, Paper, OutlinedInput, Checkbox, ListItemText, Button, Snackbar, Alert, CircularProgress, Tooltip } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MainLayout from '../../../../components/Layout/MainLayout';
import { useTranslation } from 'react-i18next';
import { NotificationListItem } from '../NotificationHistory/notification-history.api';
import httpClient from '../../../../utils/httpClient';
import { ApiSuccessResponse } from '../../../../utils/apiSchemas';

type FeatureGroup = {
  featureType: number;
  featureTypeDescription: string;
  items: NotificationListItem[];
};

type DashboardEventApiItem = {
  featureType: number;
  featureTypeDescription: string;
  createdDate: string;
};

type DashboardFeatureApiItem = {
  featureType: number;
  featureTypeDescription: string;
};

type DashboardEventsResponseDto = {
  items: DashboardEventApiItem[];
  allItems: DashboardEventApiItem[];
  refreshedAtUtc: string;
  fromCache: boolean;
  cacheTtlSeconds: number;
};

function formatDateTime(dt: string, locale: string) {
  const d = new Date(dt);
  const f = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
  return f.format(d);
}

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

function formatDateTimeShortUTC(dt: number | string) {
  const d = typeof dt === 'number' ? new Date(dt) : new Date(dt);
  const day = pad(d.getUTCDate());
  const month = pad(d.getUTCMonth() + 1);
  const year = d.getUTCFullYear();
  const hour = pad(d.getUTCHours());
  const minute = pad(d.getUTCMinutes());
  return `${day}/${month}/${year} ${hour}:${minute} UTC`;
}

function humanizeDuration(ms: number, t: (key: string, options?: Record<string, unknown>) => string) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  const w = Math.floor(d / 7);
  const mo = Math.floor(d / 30);
  const y = Math.floor(d / 365);
  if (y > 0) return t('dashboards_duration_year', { count: y });
  if (mo > 0) return t('dashboards_duration_month', { count: mo });
  if (w > 0) return t('dashboards_duration_week', { count: w });
  if (d > 0) return t('dashboards_duration_day', { count: d });
  if (h > 0) return t('dashboards_duration_hour', { count: h });
  if (m > 0) return t('dashboards_duration_minute', { count: m });
  return t('dashboards_duration_second', { count: s });
}

function humanizeSince(dt: string, t: (key: string, options?: Record<string, unknown>) => string) {
  const d = new Date(dt);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days <= 0) return t('dashboards_since_today');
  if (days === 1) return t('dashboards_since_yesterday');
  const years = Math.floor(days / 365);
  if (years >= 1) return t('dashboards_since_year_ago', { count: years });
  const months = Math.floor(days / 30);
  if (months >= 1) return t('dashboards_since_month_ago', { count: months });
  return t('dashboards_since_day_ago', { count: days });
}

function computeIntervals(items: NotificationListItem[]) {
  const sorted = [...items].sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
  const intervals = [] as number[];
  for (let i = 0; i < sorted.length - 1; i++) {
    const delta = Math.abs(new Date(sorted[i].createdDate).getTime() - new Date(sorted[i + 1].createdDate).getTime());
    intervals.push(delta);
  }
  const avg = intervals.length ? Math.round(intervals.reduce((acc, v) => acc + v, 0) / intervals.length) : 0;
  const min = intervals.length ? Math.min(...intervals) : 0;
  const max = intervals.length ? Math.max(...intervals) : 0;
  return { intervals, average: avg, min, max };
}

const USE_MOCK = false;

const FEATURES = [
  'Express Entry Draws',
  'Ontario',
  'Manitoba',
  'British Columbia',
  'Alberta'
];

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function buildMockData(locale: string, notificationTypeDescription: string): NotificationListItem[] {
  const now = Date.now();
  const days = 120;
  const items: NotificationListItem[] = [];
  let ft = 1;
  for (const name of FEATURES) {
    const base = rand(1, 4);
    for (let d = 0; d < days; d++) {
      const events = rand(0, base);
      for (let e = 0; e < events; e++) {
        const hour = rand(0, 23);
        const minute = rand(0, 59);
        const created = new Date(now - d * 24 * 60 * 60 * 1000);
        created.setHours(hour, minute, 0, 0);
        const iso = created.toISOString();
        items.push({
          featureType: ft,
          notificationType: 0,
          featureTypeDescription: name,
          notificationTypeDescription,
          createdDate: iso,
          createdDateFormatted: formatDateTime(iso, locale),
          success: true,
          resolved: false
        } as NotificationListItem);
      }
    }
    // Ensure recent events exist for this feature (avoid empty periods)
    const extraEvents = rand(2, 5);
    for (let k = 0; k < extraEvents; k++) {
      const recentDays = rand(0, 5);
      const hour = rand(0, 23);
      const minute = rand(0, 59);
      const created = new Date(now - recentDays * 24 * 60 * 60 * 1000);
      created.setHours(hour, minute, 0, 0);
      const iso = created.toISOString();
      items.push({
        featureType: ft,
        notificationType: 0,
        featureTypeDescription: name,
        notificationTypeDescription,
        createdDate: iso,
        createdDateFormatted: formatDateTime(iso, locale),
        success: true,
        resolved: false
      } as NotificationListItem);
    }
    ft++;
  }
  return items;
}

const Dashboards: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<NotificationListItem[]>([]);
  const [allData, setAllData] = useState<NotificationListItem[]>([]);
  const [availableFeatures, setAvailableFeatures] = useState<DashboardFeatureApiItem[]>([]);
  const theme = useTheme();
  const [periodDays, setPeriodDays] = useState(90);
  const [granularity, setGranularity] = useState<'hora' | 'dia' | 'semana' | 'mes'>('semana');
  const [tooltip, setTooltip] = useState<{ open: boolean; x: number; y: number; index: number } | null>(null);
  const [pieTooltip, setPieTooltip] = useState<{ x: number; y: number; index: number } | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [pieHoverIndex, setPieHoverIndex] = useState<number | null>(null);
  const [showClearLabel, setShowClearLabel] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'pt-BR';
  const periodDaysRef = useRef(periodDays);
  const granularityRef = useRef(granularity);
  const selectedFeaturesRef = useRef(selectedFeatures);
  const showClearLabelRef = useRef(showClearLabel);

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleExport = async () => {
    if (USE_MOCK) return;
    if (!availableFeatures.length) return;
    try {
      setExporting(true);

      const currentPeriodDays = periodDaysRef.current;
      const currentSelectedFeatures = selectedFeaturesRef.current;
      const currentShowClearLabel = showClearLabelRef.current;
      const currentGranularity = granularityRef.current;

      const periodEnum = (() => {
        switch (currentPeriodDays) {
          case 7: return 1;
          case 30: return 2;
          case 60: return 3;
          case 90: return 4;
          case 180: return 5;
          default: return 2;
        }
      })();
      const params = new URLSearchParams();
      params.append('period', String(periodEnum));
      const granEnumExport = (() => {
        switch (currentGranularity) {
          case 'hora': return 1;
          case 'dia': return 2;
          case 'semana': return 3;
          case 'mes': return 4;
          default: return 2;
        }
      })();
      params.append('granularity', String(granEnumExport));

      if (currentShowClearLabel && currentSelectedFeatures.length === 0) {
        params.append('features', '0');
      } else {
        const selectedIds = availableFeatures
          .filter(f => currentSelectedFeatures.includes(f.featureTypeDescription))
          .map(f => f.featureType);
        selectedIds.forEach(id => params.append('features', String(id)));
      }

      params.append('tzOffsetMinutes', String(-new Date().getTimezoneOffset()));
      params.append('locale', locale);

      const res = await httpClient.get(`/api/v1/portal/dashboards/events/export?${params.toString()}`, { responseType: 'blob' as any });

      const contentDisposition = (res.headers?.['content-disposition'] ?? '') as string;
      let filename = 'dashboards-events.xlsx';
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
      if (match?.[1]) {
        try { filename = decodeURIComponent(match[1]); } catch { filename = match[1]; }
      }

      const blob = new Blob([res.data], { type: res.headers?.['content-type'] ?? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: t('dashboards_export_success'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('dashboards_export_error'), severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    let active = true;
    if (USE_MOCK) {
      const mockFeatures: DashboardFeatureApiItem[] = FEATURES.map((name, idx) => ({
        featureType: idx + 1,
        featureTypeDescription: name
      }));
      const mock = buildMockData(locale, t('dashboards_notification_type_update'));
      if (active) {
        setAvailableFeatures(mockFeatures);
          const next = mockFeatures.map(x => x.featureTypeDescription);
          selectedFeaturesRef.current = next;
          showClearLabelRef.current = false;
          setSelectedFeatures(next);
        setShowClearLabel(false);
        setData(mock);
        setAllData(mock);
      }
      return () => { active = false; };
    }
    httpClient.get<ApiSuccessResponse<DashboardFeatureApiItem[]>>(`/api/v1/portal/dashboards/features`)
      .then((res) => {
        if (!active) return;
        const list = res.data?.data ?? [];
        setAvailableFeatures(list);
        const next = list.map(x => x.featureTypeDescription);
        selectedFeaturesRef.current = next;
        showClearLabelRef.current = false;
        setSelectedFeatures(next);
        setShowClearLabel(false);
      })
      .catch(() => {
        if (!active) return;
        setAvailableFeatures([]);
        selectedFeaturesRef.current = [];
        showClearLabelRef.current = false;
        setSelectedFeatures([]);
        setData([]);
        setAllData([]);
      });
    return () => {
      active = false;
    };
  }, [locale, t]);

  useEffect(() => {
    let active = true;
    if (USE_MOCK) return () => { active = false; };
    if (!availableFeatures.length) return () => { active = false; };
    const periodEnum = (() => {
      switch (periodDays) {
        case 7: return 1; // LAST_7_DAYS
        case 30: return 2; // LAST_30_DAYS
        case 60: return 3; // LAST_60_DAYS
        case 90: return 4; // LAST_90_DAYS
        case 180: return 5; // LAST_6_MONTHS
        default: return 2;
      }
    })();
    const granEnum = (() => {
      switch (granularity) {
        case 'hora': return 1;
        case 'dia': return 2;
        case 'semana': return 3;
        case 'mes': return 4;
        default: return 2;
      }
    })();
    const params = new URLSearchParams();
    params.append('period', String(periodEnum));
    params.append('granularity', String(granEnum));

    if (showClearLabel && selectedFeatures.length === 0) {
      params.append('features', '0');
    } else {
      const selectedIds = availableFeatures
        .filter(f => selectedFeatures.includes(f.featureTypeDescription))
        .map(f => f.featureType);
      selectedIds.forEach(id => params.append('features', String(id)));
    }

    httpClient.get<ApiSuccessResponse<DashboardEventsResponseDto>>(`/api/v1/portal/dashboards/events?${params.toString()}`)
      .then((res) => {
        if (!active) return;
        const api = res.data?.data;
        const filteredItems = api?.items ?? [];
        const baseItems = api?.allItems ?? [];
        const toList = (arr: DashboardEventApiItem[]): NotificationListItem[] => arr.map((x) => ({
          featureType: x.featureType,
          notificationType: 0,
          featureTypeDescription: x.featureTypeDescription,
          notificationTypeDescription: t('dashboards_notification_type_update'),
          createdDate: x.createdDate,
          createdDateFormatted: formatDateTime(x.createdDate, locale),
          success: true,
          resolved: false
        }));
        const mappedFiltered = toList(filteredItems.length ? filteredItems : baseItems);
        const mappedAll = toList(baseItems.length ? baseItems : filteredItems);
        setData(mappedFiltered);
        setAllData(mappedAll);
      })
      .catch(() => {
        if (!active) return;
        setData([]);
        setAllData([]);
      });

    return () => {
      active = false;
    };
  }, [availableFeatures, granularity, locale, periodDays, selectedFeatures, showClearLabel, t]);

  const groupedFiltered = useMemo(() => {
    const byFeature = new Map<number, FeatureGroup>();
    for (const item of data) {
      const g = byFeature.get(item.featureType);
      if (g) {
        g.items.push(item);
      } else {
        byFeature.set(item.featureType, {
          featureType: item.featureType,
          featureTypeDescription: item.featureTypeDescription,
          items: [item]
        });
      }
    }
    return Array.from(byFeature.values()).map((g) => ({
      ...g,
      items: g.items.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    }))
    .sort((a, b) => a.featureTypeDescription.localeCompare(b.featureTypeDescription));
  }, [data]);

  const groupedAll = useMemo(() => {
    const byFeature = new Map<number, FeatureGroup>();
    for (const item of allData) {
      const g = byFeature.get(item.featureType);
      if (g) {
        g.items.push(item);
      } else {
        byFeature.set(item.featureType, {
          featureType: item.featureType,
          featureTypeDescription: item.featureTypeDescription,
          items: [item]
        });
      }
    }
    return Array.from(byFeature.values()).map((g) => ({
      ...g,
      items: g.items.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    }))
    .sort((a, b) => a.featureTypeDescription.localeCompare(b.featureTypeDescription));
  }, [allData]);

  const featureNames = useMemo(() => availableFeatures.map((f) => f.featureTypeDescription), [availableFeatures]);

  const buckets = useMemo(() => {
    const now = Date.now();
    const start = now - periodDays * 24 * 60 * 60 * 1000;
    const dayMs = 24 * 60 * 60 * 1000;
    const bucketStarts: number[] = [];

    if (granularity === 'mes') {
      let d = new Date(start);
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1, 0, 0, 0, 0));
      while (d.getTime() <= now) {
        bucketStarts.push(d.getTime());
        d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1, 0, 0, 0, 0));
      }
    } else if (granularity === 'semana') {
      let d = new Date(start);
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
      const diff = (d.getUTCDay() + 6) % 7;
      d = new Date(d.getTime() - diff * dayMs);
      while (d.getTime() <= now) {
        bucketStarts.push(d.getTime());
        d = new Date(d.getTime() + 7 * dayMs);
      }
    } else {
      const step = granularity === 'dia' ? dayMs : 60 * 60 * 1000;
      const count = Math.floor((now - start) / step) + 1;
      for (let i = 0; i < count; i++) bucketStarts.push(start + i * step);
    }

    const arr: { ts: number; counts: Record<string, number> }[] = bucketStarts.map((ts) => {
      const counts: Record<string, number> = {};
      for (const name of selectedFeatures) counts[name] = 0;
      return { ts, counts };
    });
    for (const g of groupedFiltered) {
      if (!selectedFeatures.includes(g.featureTypeDescription)) continue;
      for (const item of g.items) {
        const t = new Date(item.createdDate).getTime();
        if (t < start || t > now) continue;
        let idx = bucketStarts.length - 1;
        while (idx >= 0 && bucketStarts[idx] > t) idx--;
        if (idx < 0) continue;
        const key = g.featureTypeDescription;
        arr[idx].counts[key] = (arr[idx].counts[key] ?? 0) + 1;
      }
    }
    return arr;
  }, [groupedFiltered, granularity, periodDays, selectedFeatures]);

  const activeFeatures = useMemo(
    () => (selectedFeatures.length ? selectedFeatures : (showClearLabel ? [] : featureNames)),
    [featureNames, selectedFeatures, showClearLabel]
  );
  const totals = useMemo(() => {
    const t: Record<string, number> = {};
    for (const name of activeFeatures) {
      let sum = 0;
      for (const b of buckets) sum += b.counts[name] || 0;
      t[name] = sum;
    }
    return t;
  }, [buckets, activeFeatures]);

  const chartWidth = 1200;
  const chartHeight = 320;
  const margin = { top: 20, right: 20, bottom: 40, left: 50 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;
  const maxY = Math.max(1, ...buckets.map((b) => Math.max(0, ...activeFeatures.map((n) => b.counts[n]))));
  const xFor = (i: number) => margin.left + (i / Math.max(1, buckets.length - 1)) * innerWidth;
  const yFor = (v: number) => margin.top + innerHeight - (v / maxY) * innerHeight;
  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary ? theme.palette.secondary.main : theme.palette.info.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main
  ];

  const sumTotals = activeFeatures.reduce((acc, n) => acc + (totals[n] || 0), 0);
  const relRanking = activeFeatures
    .map((name, fi) => {
      const value = totals[name] || 0;
      const percent = sumTotals ? Math.round((value / sumTotals) * 100) : 0;
      return { name, value, percent, color: colors[fi % colors.length] };
    })
    .sort((a, b) => b.value - a.value);

  return (
    <MainLayout>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t('dashboards_title')}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item component={'div' as React.ElementType} xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">{t('dashboards_reports_title')}</Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
                    disabled={exporting}
                    onClick={handleExport}
                    sx={{ marginLeft: 'auto' }}
                  >
                    {t('dashboards_export_button')}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <FormControl size="small">
                    <InputLabel>{t('dashboards_filters_period_label')}</InputLabel>
                    <Select
                      label={t('dashboards_filters_period_label')}
                      value={periodDays}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        periodDaysRef.current = val;
                        setPeriodDays(val);
                      }}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={7}>{t('dashboards_filters_period_last_7_days')}</MenuItem>
                      <MenuItem value={30}>{t('dashboards_filters_period_last_30_days')}</MenuItem>
                      <MenuItem value={60}>{t('dashboards_filters_period_last_60_days')}</MenuItem>
                      <MenuItem value={90}>{t('dashboards_filters_period_last_90_days')}</MenuItem>
                      <MenuItem value={180}>{t('dashboards_filters_period_last_6_months')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small">
                    <InputLabel>{t('dashboards_filters_granularity_label')}</InputLabel>
                    <Select
                      label={t('dashboards_filters_granularity_label')}
                      value={granularity}
                      onChange={(e) => {
                        const val = e.target.value as 'hora' | 'dia' | 'semana' | 'mes';
                        granularityRef.current = val;
                        setGranularity(val);
                      }}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={'hora'}>{t('dashboards_filters_granularity_hour')}</MenuItem>
                      <MenuItem value={'dia'}>{t('dashboards_filters_granularity_day')}</MenuItem>
                      <MenuItem value={'semana'}>{t('dashboards_filters_granularity_week')}</MenuItem>
                      <MenuItem value={'mes'}>{t('dashboards_filters_granularity_month')}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: 360, maxWidth: '100%' }}>
                    <InputLabel>{t('dashboards_filters_features_label')}</InputLabel>
                    <Select
                      multiple
                      value={selectedFeatures}
                      onChange={(e) => {
                        const val = e.target.value as string[];
                        if (val.includes('__ALL__')) {
                          selectedFeaturesRef.current = featureNames;
                          showClearLabelRef.current = false;
                          setSelectedFeatures(featureNames);
                          setShowClearLabel(false);
                          return;
                        }
                        if (val.includes('__CLEAR__')) {
                          selectedFeaturesRef.current = [];
                          showClearLabelRef.current = true;
                          setSelectedFeatures([]);
                          setShowClearLabel(true);
                          return;
                        }
                        const next = val.filter((v) => v !== '__ALL__' && v !== '__CLEAR__');
                        selectedFeaturesRef.current = next;
                        showClearLabelRef.current = false;
                        setSelectedFeatures(next);
                        setShowClearLabel(false);
                      }}
                      input={<OutlinedInput label={t('dashboards_filters_features_label')} />}
                      renderValue={(selected) => (
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedFeatures.length
                            ? selected.join(', ')
                            : (showClearLabel ? t('dashboards_filters_features_render_clear_all') : t('dashboards_filters_features_render_select'))}
                        </Box>
                      )}
                    >
                      <MenuItem value="__ALL__"
                        onClick={(ev) => {
                          ev.preventDefault();
                          ev.stopPropagation();
                          const isAll = selectedFeatures.length === featureNames.length;
                          const next = isAll ? [] : featureNames;
                          selectedFeaturesRef.current = next;
                          showClearLabelRef.current = false;
                          setSelectedFeatures(next);
                          setShowClearLabel(false);
                        }}
                      >
                        <Checkbox checked={selectedFeatures.length === featureNames.length} />
                        <ListItemText primary={t('dashboards_filters_features_select_all')} />
                      </MenuItem>
                      <MenuItem value="__CLEAR__">
                        <Checkbox checked={selectedFeatures.length === 0} />
                        <ListItemText primary={t('dashboards_filters_features_clear_all')} />
                      </MenuItem>
                      {featureNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox checked={selectedFeatures.indexOf(name) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 700px', minWidth: 520, maxWidth: '100%' }}>
                    <svg
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      width="100%"
                      height={chartHeight}
                      onMouseMove={(e) => {
                        const rect = (e.currentTarget as SVGElement).getBoundingClientRect();
                        const scaleX = chartWidth / rect.width;
                        const xView = (e.clientX - rect.left) * scaleX;
                        const i = Math.round(((xView - margin.left) / innerWidth) * (buckets.length - 1));
                        const clamped = Math.max(0, Math.min(buckets.length - 1, i));
                        setTooltip({ open: true, x: e.clientX, y: e.clientY, index: clamped });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <line x1={margin.left} y1={chartHeight - margin.bottom} x2={chartWidth - margin.right} y2={chartHeight - margin.bottom} stroke="#ccc" />
                      <line x1={margin.left} y1={margin.top} x2={margin.left} y2={chartHeight - margin.bottom} stroke="#ccc" />

                      {Array.from({ length: 6 }).map((_, t) => {
                        const i = Math.round((t / 5) * (buckets.length - 1));
                        const x = xFor(i);
                        const d = new Date(buckets[i]?.ts ?? Date.now());
                        const label = formatDateTimeShortUTC(d.toISOString());
                        return (
                          <g key={t}>
                            <line x1={x} y1={chartHeight - margin.bottom} x2={x} y2={chartHeight - margin.bottom + 6} stroke="#888" />
                            <text x={x} y={chartHeight - margin.bottom + 20} fontSize={11} textAnchor="middle" fill="#666">{label}</text>
                          </g>
                        );
                      })}

                      {[0, maxY].map((v, idx) => (
                        <g key={idx}>
                          <line x1={margin.left - 6} y1={yFor(v)} x2={margin.left} y2={yFor(v)} stroke="#888" />
                          <text x={margin.left - 10} y={yFor(v) + 4} fontSize={11} textAnchor="end" fill="#666">{v}</text>
                        </g>
                      ))}

                      {activeFeatures.map((name, fi) => {
                        const path = buckets.map((b, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(b.counts[name])}`).join(' ');
                        return <path key={name} d={path} fill="none" stroke={colors[fi % colors.length]} strokeWidth={2} />;
                      })}
                      {tooltip && (
                        <g>
                          {activeFeatures.map((name, fi) => (
                            <circle key={name} cx={xFor(tooltip.index)} cy={yFor(buckets[tooltip.index].counts[name])} r={4} fill={colors[fi % colors.length]} />
                          ))}
                        </g>
                      )}
                    </svg>
                  </Box>
                  <Paper sx={{ p: 2, minWidth: 240 }} elevation={2}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('dashboards_ranking_percent_title')}</Typography>
                    {relRanking.map((r) => (
                      <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, bgcolor: r.color }} />
                          <Typography variant="body2">{r.name}</Typography>
                        </Box>
                        <Typography variant="body2">{r.percent}%</Typography>
                      </Box>
                    ))}
                  </Paper>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {activeFeatures.map((name, fi) => {
                    const amount = totals[name] ?? 0;
                    const percent = sumTotals ? Math.round((amount / sumTotals) * 100) : 0;
                    const color = colors[fi % colors.length];
                    return (
                      <Tooltip
                        key={name}
                        arrow
                        title={
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, bgcolor: color }} />
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>{name}</Typography>
                            </Box>
                            <Typography variant="body2">{name}: {amount}</Typography>
                            <Typography variant="body2">{t('dashboards_pie_tooltip_percent_label')} {percent}%</Typography>
                          </Box>
                        }
                      >
                        <Chip label={`${name} (${amount})`} sx={{ backgroundColor: color, color: '#fff' }} />
                      </Tooltip>
                    );
                  })}
                </Box>

                {tooltip && buckets[tooltip.index] && (
                  <Paper sx={{ position: 'fixed', left: tooltip.x + 12, top: tooltip.y + 12, p: 1, minWidth: 200, pointerEvents: 'none' }} elevation={3}>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{formatDateTimeShortUTC(buckets[tooltip.index].ts)}</Typography>
                    {activeFeatures.map((name, fi) => (
                      <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: colors[fi % colors.length] }} />
                        <Typography variant="body2">{name}: {buckets[tooltip.index].counts[name]}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item component={'div' as React.ElementType} xs={12} md={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6">{t('dashboards_pie_charts_title')}</Typography>
                  <FormControl size="small">
                    <InputLabel>{t('dashboards_filters_period_label')}</InputLabel>
                    <Select
                      label={t('dashboards_filters_period_label')}
                      value={periodDays}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        periodDaysRef.current = val;
                        setPeriodDays(val);
                      }}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value={7}>{t('dashboards_filters_period_last_7_days')}</MenuItem>
                      <MenuItem value={30}>{t('dashboards_filters_period_last_30_days')}</MenuItem>
                      <MenuItem value={60}>{t('dashboards_filters_period_last_60_days')}</MenuItem>
                      <MenuItem value={90}>{t('dashboards_filters_period_last_90_days')}</MenuItem>
                      <MenuItem value={180}>{t('dashboards_filters_period_last_6_months')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
                  <Box>
                    <svg width={480} height={360} viewBox="0 0 480 360">
                      {(() => {
                        const now = Date.now();
                        const start = now - periodDays * 24 * 60 * 60 * 1000;
                        const counts = featureNames.map((name) => {
                          let c = 0;
                        for (const g of groupedFiltered) {
                            if (g.featureTypeDescription !== name) continue;
                            for (const it of g.items) {
                              const t = new Date(it.createdDate).getTime();
                              if (t >= start && t <= now) c++;
                            }
                          }
                          return c;
                        });
                        const total = counts.reduce((a, b) => a + b, 0);
                        const cx = 200, cy = 180, r = 120;
                        let angle = -Math.PI / 2;
                        const hexToRgb = (hex: string) => {
                          const h = hex.replace('#', '');
                          const bigint = parseInt(h.length === 3 ? h.split('').map((ch) => ch + ch).join('') : h, 16);
                          const r = (bigint >> 16) & 255;
                          const g = (bigint >> 8) & 255;
                          const b = bigint & 255;
                          return { r, g, b };
                        };
                        const getTextColor = (bg: string) => {
                          try {
                            const { r: rr, g: gg, b: bb } = hexToRgb(bg);
                            const lum = 0.2126 * rr + 0.7152 * gg + 0.0722 * bb;
                            return lum > 150 ? '#000' : '#fff';
                          } catch {
                            return '#fff';
                          }
                        };
                        const wrapLabel = (txt: string, max = 12, maxLines = 3) => {
                          const words = txt.split(' ');
                          const lines: string[] = [];
                          let current = '';
                          for (const w of words) {
                            if ((current + (current ? ' ' : '') + w).length <= max) {
                              current = current ? current + ' ' + w : w;
                            } else {
                              if (current) lines.push(current);
                              current = w;
                            }
                            if (lines.length === maxLines - 1 && current.length > max) break;
                          }
                          if (current) lines.push(current);
                          if (lines.length > maxLines) return lines.slice(0, maxLines);
                          return lines;
                        };
                        const paths: React.ReactNode[] = [];
                        for (let i = 0; i < counts.length; i++) {
                          if (counts[i] <= 0) continue;
                          const frac = total > 0 ? counts[i] / total : 0;
                          const sweep = frac * Math.PI * 2;
                          const percent = total > 0 ? Math.round(frac * 100) : 0;
                          const sliceColor = colors[i % colors.length];
                          const textColor = getTextColor(sliceColor as string);
                          const labelLines = wrapLabel(featureNames[i]);

                          if (total > 0 && counts[i] === total) {
                            paths.push(
                              <g key={i}>
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={r}
                                  fill={colors[i % colors.length]}
                                  opacity={pieHoverIndex === null ? 1 : (pieHoverIndex === i ? 1 : 0.3)}
                                  onMouseEnter={(e) => {
                                    setPieHoverIndex(i);
                                    setPieTooltip({ x: e.clientX, y: e.clientY, index: i });
                                  }}
                                  onMouseMove={(e) => {
                                    setPieTooltip({ x: e.clientX, y: e.clientY, index: i });
                                  }}
                                  onMouseLeave={() => { setPieHoverIndex(null); setPieTooltip(null); }}
                                />
                                <text x={cx} y={cy} fontSize={12} textAnchor="middle" dominantBaseline="middle" fill={textColor} pointerEvents="none">
                                  {labelLines.map((ln, li) => (
                                    <tspan key={li} x={cx} dy={li === 0 ? 0 : 14}>{ln}</tspan>
                                  ))}
                                  <tspan x={cx} dy={14}>{percent}%</tspan>
                                </text>
                              </g>
                            );
                            break;
                          }

                          const x1 = cx + r * Math.cos(angle);
                          const y1 = cy + r * Math.sin(angle);
                          const x2 = cx + r * Math.cos(angle + sweep);
                          const y2 = cy + r * Math.sin(angle + sweep);
                          const largeArc = sweep > Math.PI ? 1 : 0;
                          const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                          const mid = angle + sweep / 2;
                          const tx = cx + (r * 0.6) * Math.cos(mid);
                          const ty = cy + (r * 0.6) * Math.sin(mid);
                          paths.push(
                            <g key={i}>
                              <path
                                d={d}
                                fill={colors[i % colors.length]}
                                opacity={pieHoverIndex === null ? 1 : (pieHoverIndex === i ? 1 : 0.3)}
                                onMouseEnter={(e) => {
                                  setPieHoverIndex(i);
                                  setPieTooltip({ x: e.clientX, y: e.clientY, index: i });
                                }}
                                onMouseMove={(e) => {
                                  setPieTooltip({ x: e.clientX, y: e.clientY, index: i });
                                }}
                                onMouseLeave={() => { setPieHoverIndex(null); setPieTooltip(null); }}
                              />
                              <text x={tx} y={ty} fontSize={12} textAnchor="middle" fill={textColor} pointerEvents="none">
                                {labelLines.map((ln, li) => (
                                  <tspan key={li} x={tx} dy={li === 0 ? 0 : 14}>{ln}</tspan>
                                ))}
                                <tspan x={tx} dy={14}>{percent}%</tspan>
                              </text>
                            </g>
                          );
                          angle += sweep;
                        }
                        return paths;
                      })()}
                    </svg>
                  </Box>
                  {(() => {
                    const now = Date.now();
                    const start = now - periodDays * 24 * 60 * 60 * 1000;
                    const counts = featureNames.map((name) => {
                      let c = 0;
                      for (const g of groupedFiltered) {
                        if (g.featureTypeDescription !== name) continue;
                        for (const it of g.items) {
                          const t = new Date(it.createdDate).getTime();
                          if (t >= start && t <= now) c++;
                        }
                      }
                      return c;
                    });
                    const total = counts.reduce((a, b) => a + b, 0);
                    const ranking = featureNames.map((name, i) => {
                      const amount = counts[i] ?? 0;
                      const percent = total > 0 ? Math.round((amount / total) * 100) : 0;
                      return { name, percent, color: colors[i % colors.length] };
                    }).sort((a, b) => b.percent - a.percent);
                    return (
                      <Paper sx={{ p: 2, minWidth: 220 }} elevation={2}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('dashboards_ranking_percent_title')}</Typography>
                        {ranking.map((r) => (
                          <Box key={r.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 10, height: 10, bgcolor: r.color }} />
                              <Typography variant="body2">{r.name}</Typography>
                            </Box>
                            <Typography variant="body2">{r.percent}%</Typography>
                          </Box>
                        ))}
                      </Paper>
                    );
                  })()}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {(() => {
                    const now = Date.now();
                    const start = now - periodDays * 24 * 60 * 60 * 1000;
                    const counts = featureNames.map((name) => {
                      let c = 0;
                      for (const g of groupedFiltered) {
                        if (g.featureTypeDescription !== name) continue;
                        for (const it of g.items) {
                          const t = new Date(it.createdDate).getTime();
                          if (t >= start && t <= now) c++;
                        }
                      }
                      return c;
                    });
                    const total = counts.reduce((a, b) => a + b, 0);
                    return featureNames.map((name, fi) => {
                      const amount = counts[fi] ?? 0;
                      const percent = total > 0 ? Math.round((counts[fi] / total) * 100) : 0;
                      const color = colors[fi % colors.length];
                      return (
                        <Tooltip
                          key={name}
                          arrow
                          title={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: color }} />
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>{name}</Typography>
                              </Box>
                              <Typography variant="body2">{name}: {amount}</Typography>
                              <Typography variant="body2">{t('dashboards_pie_tooltip_percent_label')} {percent}%</Typography>
                            </Box>
                          }
                        >
                          <Chip
                            label={`${name} (${amount})`}
                            onMouseEnter={() => setPieHoverIndex(fi)}
                            onMouseLeave={() => setPieHoverIndex(null)}
                            sx={{
                              backgroundColor: color,
                              color: '#fff',
                              opacity: pieHoverIndex === null ? 1 : (pieHoverIndex === fi ? 1 : 0.35)
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </Box>
                {pieTooltip && (() => {
                  const now = Date.now();
                  const start = now - periodDays * 24 * 60 * 60 * 1000;
                  const counts = featureNames.map((name) => {
                    let c = 0;
                    for (const g of groupedFiltered) {
                      if (g.featureTypeDescription !== name) continue;
                      for (const it of g.items) {
                        const t = new Date(it.createdDate).getTime();
                        if (t >= start && t <= now) c++;
                      }
                    }
                    return c;
                  });
                  const total = counts.reduce((a, b) => a + b, 0);
                  const idx = pieTooltip.index;
                  const name = featureNames[idx];
                  const amount = counts[idx] ?? 0;
                  const percent = total > 0 ? Math.round((amount / total) * 100) : 0;
                  const color = colors[idx % colors.length];
                  return (
                    <Paper sx={{ position: 'fixed', left: pieTooltip.x + 12, top: pieTooltip.y + 12, p: 1, minWidth: 220, pointerEvents: 'none' }} elevation={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, bgcolor: color }} />
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{name}</Typography>
                      </Box>
                      <Typography variant="body2">{name}: {amount}</Typography>
                      <Typography variant="body2">{t('dashboards_pie_tooltip_percent_label')} {percent}%</Typography>
                    </Paper>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>

          
          

          <Grid item component={'div' as React.ElementType} xs={12} md={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboards_intervals_title')}</Typography>
                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '25%' }}>{t('dashboards_intervals_column_feature')}</TableCell>
                      <TableCell align="right" sx={{ width: '8%' }}>{t('dashboards_intervals_column_average')}</TableCell>
                      <TableCell align="right" sx={{ width: '11%' }}>{t('dashboards_intervals_column_minimum')}</TableCell>
                      <TableCell align="right" sx={{ width: '11%' }}>{t('dashboards_intervals_column_maximum')}</TableCell>
                      <TableCell align="right" sx={{ width: '25%' }}>{t('dashboards_intervals_column_last3')}</TableCell>
                      <TableCell align="right" sx={{ width: '20%' }}>{t('dashboards_intervals_column_expectation_next')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedAll.map((g) => {
                      const stats = computeIntervals(g.items);
                      const last3 = stats.intervals.slice(0, 3).map((v) => humanizeDuration(v, t)).join(', ');
                      return (
                        <TableRow key={g.featureType}>
                          <TableCell sx={{ width: '25%' }}>{g.featureTypeDescription}</TableCell>
                          <TableCell align="right" sx={{ width: '8%' }}>{humanizeDuration(stats.average, t)}</TableCell>
                          <TableCell align="right" sx={{ width: '8%' }}>{humanizeDuration(stats.min, t)}</TableCell>
                          <TableCell align="right" sx={{ width: '8%' }}>{humanizeDuration(stats.max, t)}</TableCell>
                          <TableCell align="right" sx={{ width: '31%' }}>{last3 || '-'}</TableCell>
                          <TableCell align="right" sx={{ width: '20%' }}>{t('dashboards_intervals_expectation_soon')}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item component={'div' as React.ElementType} xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboards_ranking_title', { year: new Date().getFullYear() })}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('dashboards_ranking_column_feature')}</TableCell>
                      <TableCell align="right">{t('dashboards_ranking_column_count')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const year = new Date().getFullYear();
                      const namesAll = groupedAll.map(g => g.featureTypeDescription);
                      const list = namesAll.map((name) => {
                        let c = 0;
                        for (const g of groupedAll) {
                          if (g.featureTypeDescription !== name) continue;
                          for (const it of g.items) {
                            const d = new Date(it.createdDate);
                            if (d.getFullYear() === year) c++;
                          }
                        }
                        return { name, c };
                      }).sort((a, b) => b.c - a.c);
                      return list.map((row) => (
                        <TableRow key={row.name}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">{row.c}</TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item component={'div' as React.ElementType} xs={12} md={9}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboards_recent_events_title')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('dashboards_recent_events_column_feature')}</TableCell>
                      <TableCell>{t('dashboards_recent_events_column_local_datetime')}</TableCell>
                      <TableCell>{t('dashboards_recent_events_column_utc')}</TableCell>
                      <TableCell>{t('dashboards_recent_events_column_last_update')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedAll.map((g) => {
                      const latest = g.items[0];
                      const local = latest ? formatDateTime(latest.createdDate, locale) : '-';
                      const utc = latest ? new Date(latest.createdDate).toISOString() : '-';
                      const since = latest ? humanizeSince(latest.createdDate, t) : '-';
                      return (
                        <TableRow key={g.featureType}>
                          <TableCell>{g.featureTypeDescription}</TableCell>
                          <TableCell>{local}</TableCell>
                          <TableCell>{utc}</TableCell>
                          <TableCell>{since}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  );
};

export default Dashboards;
